import { DocumentIcon, QuestionMarkCircleIcon, InformationCircleIcon, ExclamationTriangleIcon } from "@heroicons/react/24/outline";
import Modal from "../common/Modal";
import React, { useState, useRef } from "react";
import { useStorage } from "../../hooks/useStorage";
import { serverTimestamp, Timestamp } from "firebase/firestore";
import { useTemplates } from "../../hooks/useTemplates";
import { Template, TemplateType } from "../../types/template";
import { useLanguage } from "../../contexts/LanguageContext";
import { customerKeys } from "../customers/customerKeys";
import { quotationKeys } from "../quotations/quotationKeys";
import { warrantyKeys } from "../../utils/warrantyDataMapper";
import VariablePreviewPopup from "../documents/VariablePreviewPopup";
import toast from "react-hot-toast";
import { useOrganizations } from "../../hooks/useOrganizations";

interface CreateTemplateModalProps {
  isOpen: boolean;
  onClose: () => void;
}

const templateTypes = [
  { id: 'contract', name: 'templates.createTemplateModal.contract' },
  { id: 'quotation', name: 'templates.createTemplateModal.quotation' },
  { id: 'warranty', name: 'templates.createTemplateModal.warranty' }
];

const CreateTemplateModal = ({ isOpen, onClose }: CreateTemplateModalProps) => {
  const { t } = useLanguage();
  const docFileInputRef = useRef<HTMLInputElement>(null);
  const [selectedDocFile, setSelectedDocFile] = useState<File | null>(null);
  const [templateName, setTemplateName] = useState<string>('');
  const [templateDesc, setTemplateDesc] = useState<string>('');
  const [selectedType, setSelectedType] = useState<string>('contract');
  const [selectedOrganizationId, setSelectedOrganizationId] = useState<string>('');
  const [isUploading, setIsUploading] = useState<boolean>(false);
  const [uploadSuccess, setUploadSuccess] = useState<boolean>(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const { addTemplate } = useTemplates();
  const { organizations, loading: loadingOrganizations } = useOrganizations();

  const { uploadFile } = useStorage();

  const handleDocFileSelect = () => {
    docFileInputRef.current?.click();
  };

  const handleDocFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      if (file.type !== 'application/vnd.openxmlformats-officedocument.wordprocessingml.document') {
        setErrorMessage(t("templates.createTemplateModal.errors.invalidFormat"));
        setSelectedDocFile(null);
        return;
      }
      setSelectedDocFile(file);
      setUploadSuccess(false);
      setErrorMessage(null);
    }
    // Töröljük a file input értékét, hogy ugyanazt a fájlt újra ki lehessen választani
    if (e.target.value) e.target.value = '';
  };

  const handleTypeChange = (typeId: string) => {
    setSelectedType(typeId);
  };

  const isFormValid = () => {
    return selectedDocFile && templateName.trim() !== '' && selectedOrganizationId !== '';
  };

  const handleSubmit = async () => {
    if (!isFormValid()) return;

    setIsUploading(true);
    setErrorMessage(null);

    try {
      // Fájl feltöltése Firebase Storage-ba
      const fileName = `templates/${selectedType}/${Date.now()}-${selectedDocFile!.name}`;
      const fileUrl = await uploadFile(selectedDocFile!, fileName);

      // Template adatok mentése Firestore-ba
      const newTemplate: Omit<Template, 'id'> = {
        name: templateName,
        description: templateDesc,
        type: selectedType as TemplateType,
        organizationId: selectedOrganizationId,
        fileUrl: fileUrl,
        fileName: fileName,
        createdAt: serverTimestamp() as unknown as Timestamp,
        updatedAt: serverTimestamp() as unknown as Timestamp,
      };

      // Firestore-ba mentés
      await addTemplate(newTemplate as Template);

      setUploadSuccess(true);
      toast.success(t("templates.messages.created"));

      // Késleltetett bezárás, hogy a felhasználó lássa a sikeres feltöltés visszajelzést
      setTimeout(() => {
        // Form reset
        setSelectedDocFile(null);
        setTemplateName('');
        setTemplateDesc('');
        setSelectedOrganizationId('');
        setUploadSuccess(false);
        onClose();
      }, 500);
    } catch (error) {
      console.error("Hiba a fájl feltöltése során:", error);
      setErrorMessage(t("templates.messages.error"));
      toast.error(t("templates.messages.error"));
      setUploadSuccess(false);
    } finally {
      setIsUploading(false);
    }
  };

  const getAvailableKeys = () => {
    switch (selectedType) {
      case 'contract':
        return Object.keys(customerKeys);
      case 'quotation':
        return Object.keys(quotationKeys);
      case 'warranty':
        return Object.keys(warrantyKeys);
      default:
        return [];
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={t("templates.createTemplateModal.title")}
      size="6xl"
    >
      <form className="space-y-6 p-6">
        {/* Template Basic Info Section */}
        <div className="crm-card">
          <div>
          <div className="flex items-center gap-2 px-4 py-2.5 border-b border-[var(--crm-border)] bg-[var(--crm-bg)] rounded-t-[10px]">
            <DocumentIcon className="w-4 h-4 text-gray-400" />
            <h3 className="text-[13px] font-semibold text-gray-900 dark:text-gray-50">{t("templates.createTemplateModal.basicInfo") || "Alapadatok"}</h3>
          </div>
          <div className="p-4">
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    {t("templates.createTemplateModal.name")}
                  </label>
                  <input
                    type="text"
                    className="w-full bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-2 text-sm text-gray-700 dark:text-gray-200 placeholder-gray-500 dark:placeholder-gray-400 focus:border-primary-500 focus:ring-1 focus:ring-primary-500"
                    placeholder={t("templates.createTemplateModal.namePlaceholder")}
                    value={templateName}
                    onChange={(e) => setTemplateName(e.target.value)}
                    required
                  />
                </div>

                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    {t("templates.createTemplateModal.description")}
                  </label>
                  <textarea
                    className="w-full bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-2 text-sm text-gray-700 dark:text-gray-200 placeholder-gray-500 dark:placeholder-gray-400 focus:border-primary-500 focus:ring-1 focus:ring-primary-500"
                    placeholder={t("templates.createTemplateModal.descriptionPlaceholder")}
                    rows={3}
                    value={templateDesc}
                    onChange={(e) => setTemplateDesc(e.target.value)}
                  />
                </div>

                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    {t("templates.createTemplateModal.organization")}
                  </label>
                  <select
                    className="w-full bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-2 text-sm text-gray-700 dark:text-gray-200 focus:border-primary-500 focus:ring-1 focus:ring-primary-500"
                    value={selectedOrganizationId}
                    onChange={(e) => setSelectedOrganizationId(e.target.value)}
                    required
                  >
                    <option value="">{t("templates.createTemplateModal.selectOrganization")}</option>
                    {organizations.map((org) => (
                      <option key={org.id} value={org.id}>
                        {org.name}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Document Type Section */}
        <div className="crm-card">
          <div className="flex">
            <div className="p-4 bg-gradient-to-br from-purple-100 to-pink-100 dark:from-purple-900/20 dark:to-pink-900/20 flex items-center justify-center rounded-l-lg border-r border-gray-200 dark:border-gray-600">
              <DocumentIcon className="w-5 h-5 text-purple-600 dark:text-purple-400" />
            </div>
            <div className="p-4 flex-1">
              <div className="flex items-center gap-2 mb-4">
                <h3 className="text-sm font-medium text-gray-800 dark:text-gray-200">
                  {t("templates.createTemplateModal.documentType")}
                </h3>
                <div className="flex items-center space-x-2">
                  <VariablePreviewPopup
                    popupTitle="Elérhető változók (Dinamikus rendszer)"
                    triggerClassName="h-5 w-5 text-green-600 hover:text-green-700 cursor-help transition-colors"
                  />
                  <span className="text-xs text-green-600">Új dinamikus változók</span>
                  
                  {/* Régi változók (kompatibilitás) */}
                  <div className="group relative ml-2">
                    <InformationCircleIcon className="h-5 w-5 text-gray-400 hover:text-gray-500 cursor-help transition-colors" />
                    <span className="text-xs text-gray-400 ml-1">Régi változók</span>
                    <div className="absolute left-1/2 -translate-x-1/2 top-full mt-2 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200 z-50">
                      <div className="bg-white dark:bg-gray-700 text-gray-800 dark:text-gray-200 text-xs rounded-lg py-3 px-4 shadow-lg border border-gray-200 dark:border-gray-600 h-[400px] overflow-y-auto min-w-[280px] sm:min-w-[300px] max-w-[90vw] sm:max-w-[400px] [&::-webkit-scrollbar]:w-2 [&::-webkit-scrollbar-track]:bg-gray-100 dark:[&::-webkit-scrollbar-track]:bg-gray-600 [&::-webkit-scrollbar-thumb]:bg-gray-300 dark:[&::-webkit-scrollbar-thumb]:bg-gray-500 [&::-webkit-scrollbar-thumb]:rounded-full hover:[&::-webkit-scrollbar-thumb]:bg-primary-500/50">
                        <div className="font-medium text-sm mb-2 text-orange-600">⚠️ Régi változók (kompatibilitás)</div>
                        <p className="text-gray-600 dark:text-gray-400 mb-3 leading-relaxed text-sm">
                          Ezek a régi, fix változók. Javasoljuk az új dinamikus változó rendszer használatát.
                        </p>
                        <div className="space-y-2 bg-gray-100 dark:bg-gray-600 rounded-md p-3 mb-2">
                          {getAvailableKeys().map((key, index) => (
                            <div key={index} className="flex items-center gap-2 text-gray-700 dark:text-gray-200 font-mono text-xs hover:bg-gray-200 dark:hover:bg-gray-500 rounded px-2 py-1 transition-colors">
                              <span className="text-primary-600 dark:text-primary-400">{`{{`}</span>
                              <span className="break-all">{key}</span>
                              <span className="text-primary-600 dark:text-primary-400">{`}}`}</span>
                            </div>
                          ))}
                        </div>
                        <div className="absolute left-1/2 -translate-x-1/2 -top-1 w-2 h-2 bg-white dark:bg-gray-700 border-l border-t border-gray-200 dark:border-gray-600 transform rotate-45"></div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              
              <div className="flex flex-wrap gap-3 bg-gray-50 dark:bg-gray-700 p-3 rounded-lg border border-gray-200 dark:border-gray-600">
                {templateTypes.map(type => (
                  <label key={type.id} className="flex items-center cursor-pointer">
                    <input
                      type="radio"
                      name="templateType"
                      value={type.id}
                      checked={selectedType === type.id}
                      onChange={() => handleTypeChange(type.id)}
                      className="sr-only peer"
                    />
                    <div className="flex items-center px-3 py-2 rounded-lg border border-gray-300 dark:border-gray-600 peer-checked:border-primary-500 peer-checked:bg-primary-50 dark:peer-checked:bg-primary-900/20 hover:border-gray-400 dark:hover:border-gray-500 cursor-pointer transition-all duration-200">
                      <div className={`w-4 h-4 rounded-full border flex items-center justify-center mr-2 ${selectedType === type.id
                        ? 'border-primary-600 bg-primary-500/20'
                        : 'border-gray-400 dark:border-gray-500'
                        }`}>
                        {selectedType === type.id && <div className="w-2 h-2 rounded-full bg-primary-600"></div>}
                      </div>
                      <span className="text-sm text-gray-700 dark:text-gray-300 peer-checked:text-primary-700 dark:peer-checked:text-primary-300">
                        {t(type.name)}
                      </span>
                    </div>
                  </label>
                ))}
              </div>
            </div>
          </div>
        </div>

        {/* File Upload Section */}
        <div className="crm-card">
          <div>
          <div className="flex items-center gap-2 px-4 py-2.5 border-b border-[var(--crm-border)] bg-[var(--crm-bg)] rounded-t-[10px]">
            <DocumentIcon className="w-4 h-4 text-gray-400" />
            <h3 className="text-[13px] font-semibold text-gray-900 dark:text-gray-50">{t("templates.createTemplateModal.uploadTemplate")}</h3>
          </div>
          <div className="p-4">
              <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
                {t("templates.createTemplateModal.uploadTemplateDescription")}
              </p>

              <div
                onClick={handleDocFileSelect}
                className={`border-2 border-dashed rounded-lg p-6 text-center cursor-pointer transition-colors ${selectedDocFile
                  ? 'border-primary-600 bg-primary-500/10'
                  : 'border-gray-300 dark:border-gray-600 hover:border-primary-500/50 hover:bg-gray-50 dark:hover:bg-gray-700'
                  }`}
              >
                <input
                  type="file"
                  ref={docFileInputRef}
                  className="hidden"
                  accept=".docx"
                  onChange={handleDocFileChange}
                />
                {selectedDocFile ? (
                  <div className="flex flex-col items-center">
                    <DocumentIcon className="h-12 w-12 text-primary-600 mb-2" />
                    <p className="text-primary-600 text-sm font-medium mb-1">{selectedDocFile.name}</p>
                    <p className="text-sm text-gray-600 dark:text-gray-400">
                      {Math.round(selectedDocFile.size / 1024)} KB
                    </p>
                  </div>
                ) : (
                  <>
                    <DocumentIcon className="h-12 w-12 mx-auto text-gray-400 dark:text-gray-500 mb-3" />
                    <p className="text-gray-700 dark:text-gray-300 text-sm font-medium mb-1">
                      {t("templates.createTemplateModal.uploadTemplateDescription")}
                    </p>
                    <p className="text-sm text-gray-500 dark:text-gray-400">
                      {t("templates.createTemplateModal.uploadTemplateDescription")}
                    </p>
                    <p className="text-sm text-primary-600 dark:text-primary-400 mt-3 font-medium">
                      {t("templates.createTemplateModal.supportedFormat")}
                    </p>
                  </>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Error Message */}
        {errorMessage && (
          <div className="bg-red-50 dark:bg-red-900/20 border border-red-300 dark:border-red-700 rounded-lg p-4">
            <div className="flex">
              <ExclamationTriangleIcon className="h-5 w-5 text-red-500 dark:text-red-400 flex-shrink-0 mr-2" />
              <span className="text-red-600 dark:text-red-400 text-sm">{errorMessage}</span>
            </div>
          </div>
        )}

        {/* Form Actions */}
        <div className="flex justify-end pt-4 border-t border-gray-200 dark:border-gray-600 space-x-3">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 hover:bg-gray-50 dark:hover:bg-gray-600 rounded-lg transition-colors text-sm font-medium"
            disabled={isUploading}
          >
            {t("templates.createTemplateModal.cancel")}
          </button>
          <button
            type="submit"
            onClick={handleSubmit}
            className={`px-4 py-2 rounded-lg transition-colors flex items-center text-sm font-medium ${isFormValid() && !isUploading
              ? 'bg-primary-600 text-white hover:bg-primary-700'
              : isUploading
                ? 'bg-primary-600 text-white cursor-wait'
                : 'bg-gray-200 text-gray-400 cursor-not-allowed'
              }`}
            disabled={!isFormValid() || isUploading}
          >
            {isUploading ? (
              <>
                <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
                {t("templates.createTemplateModal.uploading")}...
              </>
            ) : uploadSuccess ? (
              <>
                <svg className="h-4 w-4 mr-2 text-white" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                </svg>
                {t("templates.createTemplateModal.uploadSuccess")}
              </>
            ) : (
              t("templates.createTemplateModal.createTemplate")
            )}
          </button>
        </div>
      </form>
    </Modal>
  );
};

export default CreateTemplateModal;

